import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Image from "../../../image/image";
import moment from "moment-timezone";
import {FormattedMessage} from "react-intl";
import DashedCorners from "@default/general-components/dashed-corners";

import PlayIcon from "../../../../../assets/ui/play_black.svg?url";
import PauseIcon from "../../../../../assets/ui/pause_black.svg?url";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";

const ParagraphAudio = ({ content }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [prevVolume, setPrevVolume] = useState(1);

  const { simpleDesign } = useAccessibilityContext();

  const player = useRef();

  const audioSrc = content.fieldAudioLink?.uri?.path;

  const getTime = (time) => {
    if (!isNaN(time)) {
      return (
        Math.floor(time / 60) + ":" + ("0" + Math.floor(time % 60)).slice(-2)
      );
    }

    return null;
  };

  const goBack = (seconds) => {
    setCurrentTime((prev) => {
      let newTime = 0;
      if (prev - seconds > 0) {
        newTime = prev - seconds;
      }
      player.current.currentTime = newTime;
      return newTime;
    });
  };

  const play = () => {
    // play() returns a promise which rejects e.g. if the source can't be loaded.
    player.current?.play()?.catch((error) => console.error(error));
  };

  const pause = () => {
    player.current?.pause();
  };

  const volumeHandler = (e) => {
    setVolume(e.target.value);
    player.current.volume = e.target.value;
  };

  const toggleMute = () => {
    setVolume((prev) => {
      if (prev > 0) {
        setPrevVolume(prev);
        player.current.volume = 0;
        return 0;
      } else {
        player.current.volume = prevVolume;
        return prevVolume;
      }
    });
  };

  useEffect(() => {
    const audioPlayer = player.current;

    if (!audioPlayer) {
      return;
    }

    const syncTime = () => {
      setCurrentTime(audioPlayer.currentTime);
      setDuration(audioPlayer.duration);
    };

    const syncPlaying = () => {
      setIsPlaying(!audioPlayer.paused && !audioPlayer.ended);
    };

    // The audio element is server side rendered, so its metadata may already
    // be loaded before hydration (and before the listeners below are attached).
    syncTime();
    syncPlaying();

    audioPlayer.addEventListener("timeupdate", syncTime);
    audioPlayer.addEventListener("loadedmetadata", syncTime);
    audioPlayer.addEventListener("durationchange", syncTime);
    audioPlayer.addEventListener("play", syncPlaying);
    audioPlayer.addEventListener("pause", syncPlaying);
    audioPlayer.addEventListener("ended", syncPlaying);

    return () => {
      audioPlayer.removeEventListener("timeupdate", syncTime);
      audioPlayer.removeEventListener("loadedmetadata", syncTime);
      audioPlayer.removeEventListener("durationchange", syncTime);
      audioPlayer.removeEventListener("play", syncPlaying);
      audioPlayer.removeEventListener("pause", syncPlaying);
      audioPlayer.removeEventListener("ended", syncPlaying);
    };
  }, [audioSrc]);

  const sectionClassNames = classNames({
    'paragraph paragraph-audio': true,
    'with-image': content && content.fieldImage,
    'without-image': content && !content.fieldImage,
    'without-text': !content.fieldText
  });

  return (
    <section
      className={sectionClassNames}
      data-darkmode={content.fieldDarkMode}
    >
      <div className="container">
        <div className="row">
          {(content.fieldHeading || content.fieldSubheadline) &&
            <div className={`col-12 headline-wrapper ${(!content.fieldImage && !simpleDesign) ? 'col-lg-6' : ''}`}>
              {content.fieldHeading &&
                <h2 className={"headline"}>{content.fieldHeading}</h2>
              }
              {content.fieldSubheadline &&
                <h3 className={"subline"}>{content.fieldSubheadline}</h3>
              }
            </div>
          }
          {content.fieldImage &&
            <div className={`col-12 col-lg-5 image-section`}>
              <div className={"image-wrapper"}>
                <Image
                  data={content.fieldImage.fieldMediaImage}
                  credit={content.fieldImage.fieldCredits}
                />
              </div>
            </div>
          }
          <div className={`col-12 ${simpleDesign ? "col-lg-8" : "col-lg-6"} audio-section`}>
            <div className="height-wrapper">
              <DashedCorners />
              <div className="inner-wrapper">
                <div className="file-info">
                  {(content.fieldSubline || content.fieldPublishedOn) &&
                    <div className="infos">
                      <div className="date">
                        {content.fieldPublishedOn &&
                          <h4>
                            {moment
                              .utc(content.fieldPublishedOn.value)
                              .local()
                              .format("DD.MM.YYYY")}
                          </h4>
                        }
                      </div>
                      <div className="subline">
                        {content.fieldSubline &&
                          <h4>{content.fieldSubline}</h4>
                        }
                      </div>
                    </div>
                  }
                  {content.fieldTitleAudio &&
                    <h3>{content.fieldTitleAudio}</h3>
                  }
                </div>
                {audioSrc &&
                  <audio
                    src={audioSrc}
                    preload="metadata"
                    aria-controls="audio-controls"
                    aria-label="Audio player"
                    ref={player}
                  />
                }
                <div className="audio-controls">
                  <div className="button">
                    {!isPlaying ? (
                      <button
                        tabIndex={0}
                        role="button"
                        aria-label={"play"}
                        className="audio-toggle"
                        onClick={play}
                      >
                        <div className={"sr-only"}>
                          <FormattedMessage id={"play"} />
                        </div>
                        <img src={PlayIcon} alt={"play icon"}/>
                      </button>
                    ) : (
                      <button
                        tabIndex={0}
                        role="button"
                        aria-label={"pause"}
                        className="audio-toggle"
                        onClick={pause}
                      >
                        <div className={"sr-only"}>
                          <FormattedMessage id={"pause"} />
                        </div>
                        <img
                          className="download-icon"
                          src={PauseIcon}
                          alt={"pause icon"}
                        />
                      </button>
                    )}
                  </div>
                  <div className="bar-wrapper">
                    <div className={`progress-bar ${currentTime === 0 ? 'paused' : 'playing'}`}>
                      <div className="bar">
                        <div
                          aria-valuemin={0}
                          aria-valuemax={duration}
                          aria-valuenow={currentTime}
                          className="fill"
                          style={{
                            width: `${(100 / duration) * currentTime}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="time">
                    <div className="current">
                      {currentTime ? (
                        <span className="current-time">
                          {getTime(currentTime)} min
                        </span>
                      ) : (
                        <span className="current-time">
                          0:00 min
                        </span>
                      )}
                      {duration > 0 && <React.Fragment> / {getTime(duration)}</React.Fragment>}
                    </div>
                  </div>
                </div>
              </div>

              <div aria-live="polite" className="sr-only">
                {isPlaying ? 'Playing' : 'Paused'}
              </div>

              {Boolean(content.fieldText) &&
                <div className="text-wrapper">
                  {content.fieldText &&
                    <div
                      className="text allow-scrolling"
                      tabIndex={0}
                      dangerouslySetInnerHTML={{ __html: content.fieldText }}
                    />
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

ParagraphAudio.propTypes = {
  viewMode: PropTypes.string,
  content: PropTypes.shape({
    fieldHeading: PropTypes.string,
    fieldFile: PropTypes.shape({
      name: PropTypes.string,
      fieldDescription: PropTypes.string,
      fieldPreviewImage: PropTypes.shape({
        fieldMediaImage: PropTypes.shape({
          style: PropTypes.shape({
            url: PropTypes.string.isRequired,
          }),
        }),
      }),
      mediaFileUrl: PropTypes.shape({
        path: PropTypes.string.isRequired,
      }),
    }),
  }),
};

export default ParagraphAudio;
